import i18n from "../../../utils/translations/i18n";
import { useEffect, useState } from "react";
import { signupFormLogic } from "./formLogic";
import Styles from "./index.module.scss";
import TextBox from "../../shared/elements/textbox";
import PasswordPolicy from "../../shared/elements/passwordPolicy";
import Button from "../../shared/elements/button";
import { useNavigate } from 'react-router-dom';
import { useAuthFrontLock } from '../../../context/useAuthFrontContext';
import { useScreen } from '../../../context/useScreen';
import ErrorBar from "../../shared/elements/errorBar";
import { ERROR_CODE, FALLBACK_LOCALE, COOKIE_CONSTANT, LOCALE_MAPPING, PAGE_NAME } from "../../../utils/constant/constant";
import { useLoader } from '../../../context/useLoader';
import { deleteCookie, encodeValue } from "../../../utils/utils/utils";

const ResetPassword = () => {
    const { t } = i18n;
    const [form, setFormState] = useState(JSON.parse(JSON.stringify(signupFormLogic)));
    const authFrontLock = useAuthFrontLock();
    const screen = useScreen();
    const { showLoader, hideLoader } = useLoader();
    const {setDisplayScreen} = useScreen();
    const [successBarState, setSuccessBarState] = useState(false);
    const [errorBarState, setErrorBarState] = useState({isActive:false, errorType:'', message:''});
    const [userEmail, setUserEmail] = useState('');
    const [locale, setLocale] = useState(FALLBACK_LOCALE);

    const errorMessages = {
        [ERROR_CODE.NULL_ERROR]: t('reset_password.mandatory_field_error'),
        [ERROR_CODE.PASSWORD_HISTORY_ERROR]: t('reset_password.password_history_error'),
        [ERROR_CODE.GENERIC_ERROR]: t('reset_password.generic_error'),
    };

    useEffect(() => {
        try {
            document.title = t('reset-password.page_title');
            deleteCookie(COOKIE_CONSTANT.CURRENT_PAGE_COOKIE_KEY);
            showLoader();
            const resetInit = async () => {
                try {
                    
                    const resetPasswordSetting  = authFrontLock.getResetPasswordSettings();
                    setUserEmail(resetPasswordSetting?.email || '');
                    
                    //set locale as from cookie if not present from ui_locale params if not present fallbcak en-US
                    const localeFromCookie = localStorage.getItem(COOKIE_CONSTANT.LOCALE_COOKIE_KEY);
                    
                    const uiLocale = authFrontLock.getUILocales();
                    const localeFromParams = uiLocale && uiLocale.length > 0 ? uiLocale[0] : '';
                    const finalLocale = localeFromCookie || localeFromParams || FALLBACK_LOCALE;
                    
                    setLocale(finalLocale);
                    i18n.changeLanguage(finalLocale);
                } catch (error) {
                    hideLoader();
                }
            }
            
            resetInit();
            hideLoader();

        } catch (error) {
            hideLoader();
        }

    }, []);
    
    const handleChange = (event) => {
        setFormState({
            ...form,
            [event.target.name]: {
                ...form[event.target.name],
                value: event.target.value,
                dangerWarn: false,
                errorMessage: null
            }             
        })
    }

    const handleBlur = (event) => {
        const fieldName = event.target.name;
        const fieldObj = form[fieldName];
        const { regex, value } = fieldObj;
        const expression = new RegExp(regex);
    
        const showRegexError = value !== '' && !expression.test(value);
        const showNullError = value === '' && errorBarState.isActive && errorBarState.errorType === ERROR_CODE.NULL_ERROR;
    
        setFormState({
            ...form,
            [fieldName]: {
                ...form[fieldName],
                dangerWarn: showRegexError || showNullError || (!showNullError && userEmail.toLowerCase() === value.toLowerCase()),
                errorMessage: showRegexError 
                    ? (value.length < 8
                        ? t('reset_password.password_length_error')
                        : t('reset_password.password_policy_error'))
                    : (!showNullError && userEmail.toLowerCase() === value.toLowerCase()
                        ? t('reset_password.common_password_error')
                        : null),
            }
        });
    };

    const validateForm = () => {
        const formObj = {...form};
        let regexError = false, nullError = false;
        
        formObj.formFields.forEach(field => {
            const fieldObj = formObj[field], {regex, value, required} = fieldObj, expression = new RegExp(regex);
            formObj[field].dangerWarn = false;
            formObj[field].errorMessage = '';

            if (value !== '' && !expression.test(value)) {
                regexError = true;
                formObj[field].dangerWarn = true;
                formObj[field].errorMessage = value.length < 8
                    ? t('reset_password.password_length_error')
                    : t('reset_password.password_policy_error');
            }
            if (value === '' && required) {
                nullError = true;
                formObj[field].dangerWarn = true;
            }
        });

        if (!(regexError || nullError) && userEmail.toLowerCase() === formObj.password.value.toLowerCase()) {
            formObj.password.dangerWarn = true;
            formObj.password.errorMessage = t('reset_password.common_password_error');
            regexError = true;
        }

        if (nullError) showErrorMessage(ERROR_CODE.NULL_ERROR);
        else setErrorBarState({isActive:false, errorType:'', message:''});
        setFormState(formObj);

        return !(regexError || nullError);
    };

    
    const showErrorMessage = (error) => {
        if (errorMessages[error]) {
            setErrorBarState({ isActive: true, errorType: error, message: errorMessages[error] });
        } else {
            setErrorBarState({ isActive: true, errorType: ERROR_CODE.GENERIC_ERROR, message: errorMessages[ERROR_CODE.GENERIC_ERROR] });
        }
    };
    

    const handleSubmit = async (event) => {
        try {
            event.preventDefault();
            if(validateForm()){
                showLoader();
                const password = form.password.value;
                await authFrontLock.resetPasswordAsync({
                    password,
                    confirmPassword: password
                });

                const state = {
                    redirectURL: `${process.env.REACT_APP_SITE_BASE_URL}/${LOCALE_MAPPING[locale]}`,
                    clientId: process.env.REACT_APP_AUTHFRONT_SDK_CLIENT_ID
                };
                let additionalParams = {
                    screenType: PAGE_NAME.SIGN_IN,
                };

                const callbackUrl = `${process.env.REACT_APP_SITE_BASE_URL || 'https://stg-www.pens.com'}/auth-callback?state=${encodeValue(state)}&additional_params=${encodeValue(additionalParams)}&ui_locales=${locale}&error=${encodeURIComponent('RESET_PASSWORD_SUCCESSFUL')}&login_hint=${encodeURIComponent(userEmail)}`;

                localStorage.removeItem(COOKIE_CONSTANT.LOCALE_COOKIE_KEY);

                window.location.replace(callbackUrl);
            }
        } catch (error: any) {
            showErrorMessage(error.errorCode);
        } finally {
            hideLoader();
        }
    }

    const toggleShowPassword = (fieldName) => {
        const showPassword = !form[fieldName].showPassword;
        setFormState({
            ...form,
            [fieldName]: {
                ...form[fieldName],
                showPassword,
                type: showPassword ? 'text' : 'password'
            }         
        })
    }

    return (
        <div className={Styles.container}>
            <div className={Styles.header}>
                <h2 className={Styles.title}>{t('reset_password.set_password')}</h2>
                <div>
                    <span className={Styles.subtle}>{t('reset_password.create_new_password')}</span>
                </div>
            </div>
            {errorBarState.isActive && <ErrorBar message={errorBarState.message}/>}
            <div>
                <form className={Styles.body}>
                    {form.formFields.map(field => {
                        return (<TextBox
                            key={form[field].id}
                            id={form[field].id}
                            className={form[field].className}
                            name={form[field].name}
                            type={form[field].type}
                            max={form[field].maxLength}
                            label={t(form[field].label)}
                            placeholder={t(form[field].label)}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isRequired={form[field].required}
                            value={form[field].value}
                            dangerWarn={form[field].dangerWarn}
                            errorMessage={form[field].errorMessage}
                            showPasswordHandler={toggleShowPassword}
                            showPasswordIcon={form[field].showPasswordIcon}
                        ></TextBox>)
                    })}
                    {<div className={Styles.passwordPolicy}>
                        <div className={Styles.policyHeader}>{t('validation.password_validation_heading')}</div>
                        <PasswordPolicy className={Styles.policyContainer} password={form['password']?.value} />
                    </div>}
                    <Button id="auth-button" type="submit" onClick={handleSubmit} subClass="btn--pill" className={Styles.submitButton}>
                        {t('reset_password.submit')}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;